import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/hooks/useAuth";
import { useList } from "@refinedev/core";
import { supabase } from "@/lib/supabase";
import { PageHeader } from "@/components/forms/PageHeader";
import { MoneyInput } from "@/components/forms/MoneyInput";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { ClienteSearch } from "@/components/forms/ClienteSearch";
import { ImeiInput } from "@/components/forms/ImeiInput";
import { FinancieraPicker } from "@/components/forms/FinancieraPicker";
// validateDocumento ya no se importa (aceptamos cualquier formato de documento).

type Tipo = "moto" | "celular";
type PagoTipo = "efectivo" | "financiera";

interface Pago {
  id: string;
  tipo: PagoTipo;
  financiera_id?: string;
  financiera_nombre?: string;
  monto: number;
  numero_aprobacion?: string;
}

export function NuevaVentaPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Admin puede elegir tienda; operador usa la suya.
  const [tiendaIdAdmin, setTiendaIdAdmin] = useState<string>("");
  const { data: tiendasData } = useList({
    resource: "tiendas",
    filters: [{ field: "activo", operator: "eq", value: true }],
    pagination: { mode: "off" },
  });
  const tiendas = (tiendasData?.data ?? []) as Array<{
    id: string;
    nombre: string;
    tipo_producto: "moto" | "celular" | "mixto";
  }>;

  const tiendaId = isAdmin ? tiendaIdAdmin : (user?.tienda_id ?? "");
  const tiendaSeleccionada = tiendas.find((t) => t.id === tiendaId);
  const tipoProducto: Tipo = ((isAdmin
    ? tiendaSeleccionada?.tipo_producto
    : user?.tienda_tipo_producto) ?? "moto") as Tipo;

  const { data: vendedores } = useList({
    resource: "vendedores",
    filters: tiendaId
      ? [{ field: "tienda_id", operator: "eq", value: tiendaId }]
      : [],
    pagination: { mode: "off" },
    queryOptions: { enabled: !!tiendaId },
  });

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [ventaCreada, setVentaCreada] = useState<{ id: string; numero: number } | null>(null);

  // Paso 1: cliente
  const [cliente, setCliente] = useState({
    cliente_id: null as string | null,
    documento: "",
    nombre: "",
    telefono: "",
    ciudad: "",
    email: "",
    direccion: "",
  });

  // Paso 2: producto
  const [producto, setProducto] = useState({
    marca: "",
    modelo: "",
    color: "",
    almacenamiento: "",
    ram: "",
    vin_chasis: "",
    serial_motor: "",
    serial_bateria: "",
    placa: "",
    imei_1: "",
    imei_2: "",
    numero_serie: "",
    valor_total: 0,
    vendedor_id: "",
  });

  // Paso 3: pagos
  const [pagos, setPagos] = useState<Pago[]>([]);
  const [comentarios, setComentarios] = useState("");

  const sumaPagos = pagos.reduce((s, p) => s + p.monto, 0);
  const cuadra = sumaPagos === producto.valor_total && producto.valor_total > 0;

  // Si admin cambia de tienda con campos de producto ya llenos, regresar a step 1
  // y limpiar producto/pagos para evitar mezclar tipos (moto/celular).
  useEffect(() => {
    if (!isAdmin) return;
    if (step === 1) return;
    // Si tipoProducto cambió y ya había datos de producto, reset.
    if (producto.marca || producto.modelo || producto.valor_total > 0 || pagos.length > 0) {
      setProducto({
        marca: "",
        modelo: "",
        color: "",
        almacenamiento: "",
        ram: "",
        vin_chasis: "",
        serial_motor: "",
        serial_bateria: "",
        placa: "",
        imei_1: "",
        imei_2: "",
        numero_serie: "",
        valor_total: 0,
        vendedor_id: "",
      });
      setPagos([]);
      setStep(1);
      toast.info("Cambiaste de tienda. Vuelve a llenar los datos del producto.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoProducto]);

  function puedeAvanzar() {
    if (step === 1) {
      // Tienda obligatoria (admin elige, operador la tiene asignada).
      if (!tiendaId) return false;
      // Documento: 3+ chars (acepta letras, números, guiones para CC/CE/PA/NIT).
      // Nombre: 3+ chars.
      return cliente.documento.trim().length >= 3 && cliente.nombre.trim().length >= 3;
    }
    if (step === 2) {
      return producto.marca && producto.modelo && producto.valor_total > 0;
    }
    return cuadra;
  }

  async function guardarVenta() {
    if (!user) {
      toast.error("No has iniciado sesión");
      return;
    }
    if (!tiendaId) {
      toast.error(isAdmin ? "Selecciona una tienda" : "Tu usuario no tiene tienda asignada");
      return;
    }
    setSaving(true);

    try {
      // 1. Asegurar cliente
      let clienteId = cliente.cliente_id;
      if (!clienteId) {
        const { data, error } = await supabase
          .from("clientes")
          .upsert(
            {
              documento: cliente.documento,
              nombre_completo: cliente.nombre,
              telefono: cliente.telefono || null,
              ciudad: cliente.ciudad || null,
              email: cliente.email || null,
              direccion: cliente.direccion || null,
            },
            { onConflict: "documento" },
          )
          .select("id")
          .single();
        if (error) throw error;
        clienteId = data.id;
      }

      // 2. Crear venta
      const ventaData: Record<string, unknown> = {
        tienda_id: tiendaId,
        fecha_venta: new Date().toISOString().slice(0, 10),
        cliente_id: clienteId,
        cliente_nombre: cliente.nombre,
        cliente_documento: cliente.documento,
        cliente_telefono: cliente.telefono || null,
        cliente_ciudad: cliente.ciudad || null,
        cliente_email: cliente.email || null,
        cliente_direccion: cliente.direccion || null,
        vendedor_id: producto.vendedor_id || null,
        tipo_producto: tipoProducto,
        producto_marca: producto.marca,
        producto_modelo: producto.modelo,
        producto_color: producto.color || null,
        valor_total: producto.valor_total,
        estado: "confirmada",
        comentarios: comentarios || null,
        registrado_por: user.id,
      };

      if (tipoProducto === "moto") {
        Object.assign(ventaData, {
          vin_chasis: producto.vin_chasis || null,
          serial_motor: producto.serial_motor || null,
          serial_bateria: producto.serial_bateria || null,
          placa: producto.placa || null,
        });
      } else {
        Object.assign(ventaData, {
          producto_almacenamiento: producto.almacenamiento || null,
          producto_ram: producto.ram || null,
          imei_1: producto.imei_1 || null,
          imei_2: producto.imei_2 || null,
          numero_serie: producto.numero_serie || null,
        });
      }

      const { data: ventaRow, error: ventaErr } = await supabase
        .from("ventas")
        .insert(ventaData)
        .select("id, numero_consecutivo")
        .single();
      if (ventaErr) throw ventaErr;

      // 3. Insertar pagos
      const pagosInsert = pagos.map((p) => ({
        venta_id: ventaRow.id,
        tipo: p.tipo,
        financiera_id: p.financiera_id ?? null,
        monto: p.monto,
        numero_aprobacion: p.numero_aprobacion ?? null,
      }));
      const { error: pagosErr } = await supabase.from("venta_pagos").insert(pagosInsert);
      if (pagosErr) throw pagosErr;

      // 4. Vincular al periodo abierto
      const { data: periodo } = await supabase
        .from("periodos_cobro")
        .select("id")
        .eq("tienda_id", tiendaId)
        .eq("estado", "abierto")
        .maybeSingle();

      if (periodo) {
        await supabase
          .from("ventas")
          .update({ periodo_cobro_id: periodo.id })
          .eq("id", ventaRow.id);
      }

      setVentaCreada({ id: ventaRow.id, numero: ventaRow.numero_consecutivo });
    } catch (err) {
      toast.error("Error al guardar", { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  if (ventaCreada) {
    return (
      <div className="max-w-md mx-auto text-center py-12 space-y-4">
        <CheckCircle2 className="size-20 mx-auto text-success" />
        <h1 className="text-2xl font-bold">Venta #{ventaCreada.numero} registrada</h1>
        <p className="text-fg-secondary">
          {cliente.nombre} · {producto.marca} {producto.modelo} ·{" "}
          <MoneyDisplay value={producto.valor_total} />
        </p>
        <p className="text-sm text-fg-muted">
          Cuando cierres tu corte, esta venta entra automáticamente.
        </p>
        <div className="flex flex-col gap-2 pt-4">
          <button onClick={() => window.location.reload()} className="btn-primary">
            Registrar otra venta
          </button>
          <button onClick={() => navigate(`/ventas/${ventaCreada.id}`)} className="btn-secondary">
            Ver detalle
          </button>
          <button onClick={() => navigate("/")} className="btn-secondary">
            Ir al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <header className="flex items-center justify-between mb-6">
        <button
          onClick={() => (step > 1 ? setStep(step - 1) : navigate(-1))}
          className="flex items-center gap-1 text-fg-secondary hover:text-fg"
        >
          <ArrowLeft className="size-4" /> Atrás
        </button>
        <span className="text-sm font-medium text-fg-secondary">{step}/3</span>
      </header>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 1 && (
            <div className="space-y-4">
              <PageHeader title="¿Quién está comprando?" />
              {isAdmin && (
                <Field label="Tienda donde se registra la venta *">
                  <select
                    value={tiendaIdAdmin}
                    onChange={(e) => setTiendaIdAdmin(e.target.value)}
                    className="input"
                  >
                    <option value="">Selecciona una tienda...</option>
                    {tiendas.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre} ({t.tipo_producto})
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {!isAdmin && user?.tienda_nombre && (
                <p className="text-sm text-fg-muted">
                  Tienda: <span className="font-medium text-fg">{user.tienda_nombre}</span>
                </p>
              )}
              <div className="space-y-1">
                <label className="text-sm font-medium">Documento *</label>
                <input
                  value={cliente.documento}
                  onChange={(e) => setCliente({ ...cliente, documento: e.target.value })}
                  className="input"
                  placeholder="CC, CE, NIT, pasaporte..."
                  autoComplete="off"
                />
                <ClienteSearch
                  documento={cliente.documento}
                  onFound={(c) =>
                    setCliente({
                      cliente_id: c.id,
                      documento: c.documento,
                      nombre: c.nombre_completo,
                      telefono: c.telefono ?? "",
                      ciudad: c.ciudad ?? "",
                      email: c.email ?? "",
                      direccion: c.direccion ?? "",
                    })
                  }
                />
              </div>
              <Field label="Nombre completo *">
                <input
                  value={cliente.nombre}
                  onChange={(e) => setCliente({ ...cliente, nombre: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Teléfono">
                <input
                  value={cliente.telefono}
                  onChange={(e) => setCliente({ ...cliente, telefono: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Ciudad">
                <input
                  value={cliente.ciudad}
                  onChange={(e) => setCliente({ ...cliente, ciudad: e.target.value })}
                  className="input"
                />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <PageHeader
                title={tipoProducto === "celular" ? "¿Qué celular?" : "¿Qué moto?"}
              />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Marca *">
                  <input
                    value={producto.marca}
                    onChange={(e) => setProducto({ ...producto, marca: e.target.value })}
                    className="input"
                  />
                </Field>
                <Field label="Modelo *">
                  <input
                    value={producto.modelo}
                    onChange={(e) => setProducto({ ...producto, modelo: e.target.value })}
                    className="input"
                  />
                </Field>
              </div>
              {tipoProducto === "celular" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Almacenamiento">
                    <input
                      value={producto.almacenamiento}
                      onChange={(e) => setProducto({ ...producto, almacenamiento: e.target.value })}
                      className="input"
                      placeholder="128GB"
                    />
                  </Field>
                  <Field label="RAM">
                    <input
                      value={producto.ram}
                      onChange={(e) => setProducto({ ...producto, ram: e.target.value })}
                      className="input"
                      placeholder="8GB"
                    />
                  </Field>
                </div>
              )}
              <Field label="Color">
                <input
                  value={producto.color}
                  onChange={(e) => setProducto({ ...producto, color: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Valor de venta *">
                <MoneyInput
                  value={producto.valor_total}
                  onChange={(v) => setProducto({ ...producto, valor_total: v })}
                />
              </Field>

              <h3 className="font-medium pt-2">Identificadores</h3>
              {tipoProducto === "moto" ? (
                <div className="space-y-3">
                  <Field label="VIN / Chasis">
                    <input
                      value={producto.vin_chasis}
                      onChange={(e) => setProducto({ ...producto, vin_chasis: e.target.value.toUpperCase() })}
                      className="input font-mono"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Serial motor">
                      <input
                        value={producto.serial_motor}
                        onChange={(e) => setProducto({ ...producto, serial_motor: e.target.value })}
                        className="input"
                      />
                    </Field>
                    <Field label="Serial batería">
                      <input
                        value={producto.serial_bateria}
                        onChange={(e) => setProducto({ ...producto, serial_bateria: e.target.value })}
                        className="input"
                      />
                    </Field>
                  </div>
                  <Field label="Placa">
                    <input
                      value={producto.placa}
                      onChange={(e) => setProducto({ ...producto, placa: e.target.value.toUpperCase() })}
                      className="input"
                    />
                  </Field>
                </div>
              ) : (
                <div className="space-y-3">
                  <ImeiInput
                    value={producto.imei_1}
                    onChange={(v) => setProducto({ ...producto, imei_1: v })}
                    label="IMEI"
                  />
                  <ImeiInput
                    value={producto.imei_2}
                    onChange={(v) => setProducto({ ...producto, imei_2: v })}
                    label="IMEI 2 (dual SIM, opcional)"
                  />
                  <Field label="Número de serie">
                    <input
                      value={producto.numero_serie}
                      onChange={(e) => setProducto({ ...producto, numero_serie: e.target.value })}
                      className="input"
                    />
                  </Field>
                </div>
              )}

              <Field label="Vendedor">
                <select
                  value={producto.vendedor_id}
                  onChange={(e) => setProducto({ ...producto, vendedor_id: e.target.value })}
                  className="input"
                >
                  <option value="">Sin asignar</option>
                  {vendedores?.data.map((v) => (
                    <option key={v.id} value={v.id}>{v.nombre as string}</option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <PageHeader title="¿Cómo está pagando?" />
              <div className="card p-4 bg-bg sticky top-16 z-10">
                <p className="text-sm text-fg-muted">Total a cubrir</p>
                <MoneyDisplay value={producto.valor_total} className="text-2xl font-bold" />
              </div>

              {pagos.map((p, i) => (
                <PagoFila
                  key={p.id}
                  pago={p}
                  onChange={(updated) =>
                    setPagos((prev) => prev.map((x, idx) => (idx === i ? updated : x)))
                  }
                  onRemove={() => setPagos((prev) => prev.filter((_, idx) => idx !== i))}
                />
              ))}

              <NuevoPagoBlock
                onAdd={(p) => setPagos([...pagos, { ...p, id: crypto.randomUUID() }])}
              />

              <div className="card p-3 flex items-center justify-between">
                <span className="text-sm">Suma:</span>
                <MoneyDisplay
                  value={sumaPagos}
                  className={cuadra ? "text-success font-bold" : "text-warning font-bold"}
                />
              </div>
              {!cuadra && producto.valor_total > 0 && (
                <p className="text-sm text-warning text-center">
                  {sumaPagos < producto.valor_total
                    ? `Te faltan ${(producto.valor_total - sumaPagos).toLocaleString("es-CO")}`
                    : `Te sobran ${(sumaPagos - producto.valor_total).toLocaleString("es-CO")}`}
                </p>
              )}

              <Field label="Comentarios (opcional)">
                <textarea
                  value={comentarios}
                  onChange={(e) => setComentarios(e.target.value)}
                  className="input min-h-20"
                />
              </Field>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <footer className="pt-6">
        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={!puedeAvanzar()}
            className="btn-primary w-full"
          >
            Siguiente
          </button>
        ) : (
          <button
            onClick={guardarVenta}
            disabled={!cuadra || saving}
            className="btn-primary w-full"
          >
            {saving ? "Guardando..." : "Registrar venta"}
          </button>
        )}
      </footer>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

function PagoFila({
  pago,
  onChange,
  onRemove,
}: {
  pago: Pago;
  onChange: (p: Pago) => void;
  onRemove: () => void;
}) {
  return (
    <div className="card p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-medium text-sm">
          {pago.tipo === "efectivo" ? "💵 Efectivo" : `💳 ${pago.financiera_nombre ?? "Financiera"}`}
        </span>
        <button onClick={onRemove} className="text-fg-muted hover:text-error">
          <X className="size-4" />
        </button>
      </div>
      <MoneyInput value={pago.monto} onChange={(v) => onChange({ ...pago, monto: v })} />
      {pago.tipo === "financiera" && (
        <input
          value={pago.numero_aprobacion ?? ""}
          onChange={(e) => onChange({ ...pago, numero_aprobacion: e.target.value })}
          placeholder="N° aprobación"
          className="input"
        />
      )}
    </div>
  );
}

function NuevoPagoBlock({ onAdd }: { onAdd: (p: Omit<Pago, "id">) => void }) {
  const [open, setOpen] = useState(false);
  const [tipo, setTipo] = useState<PagoTipo | null>(null);
  const [financieraId, setFinancieraId] = useState<string | null>(null);
  const [monto, setMonto] = useState(0);
  const { data: financieras } = useList({
    resource: "financieras",
    filters: [{ field: "activo", operator: "eq", value: true }],
    pagination: { mode: "off" },
  });

  function reset() {
    setOpen(false);
    setTipo(null);
    setFinancieraId(null);
    setMonto(0);
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-secondary w-full flex items-center justify-center gap-2">
        <Plus className="size-4" /> Agregar pago
      </button>
    );
  }

  if (!tipo) {
    return (
      <div className="card p-3 space-y-2">
        <p className="text-sm font-medium">¿Cómo paga?</p>
        <button onClick={() => setTipo("efectivo")} className="card p-3 w-full text-left hover:bg-bg">
          💵 Efectivo
        </button>
        <button onClick={() => setTipo("financiera")} className="card p-3 w-full text-left hover:bg-bg">
          💳 Crédito con financiera
        </button>
        <button onClick={reset} className="text-sm text-fg-muted">Cancelar</button>
      </div>
    );
  }

  return (
    <div className="card p-3 space-y-3">
      <p className="text-sm font-medium">
        {tipo === "efectivo" ? "Efectivo" : "Crédito con financiera"}
      </p>
      {tipo === "financiera" && (
        <FinancieraPicker value={financieraId} onChange={setFinancieraId} />
      )}
      <MoneyInput value={monto} onChange={setMonto} />
      <div className="flex gap-2">
        <button
          onClick={() => {
            if (tipo === "financiera" && !financieraId) return;
            const fin = financieras?.data.find((f) => f.id === financieraId);
            onAdd({
              tipo,
              monto,
              financiera_id: financieraId ?? undefined,
              financiera_nombre: fin?.nombre as string | undefined,
            });
            reset();
          }}
          disabled={!monto || (tipo === "financiera" && !financieraId)}
          className="btn-primary flex-1"
        >
          Agregar
        </button>
        <button onClick={reset} className="btn-secondary">Cancelar</button>
      </div>
    </div>
  );
}
